import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { IconPlus, IconTrash } from '../components/Icons';

export default function Payouts() {
  const [payouts, setPayouts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form State
  const [selectedUser, setSelectedUser] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('UPI'); // 'UPI', 'Bank Transfer', 'Cash'
  const [transactionRef, setTransactionRef] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Fetch users (shops & drivers)
    const qUsers = query(collection(db, 'users'));
    const unsubUsers = onSnapshot(qUsers, snap => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setUsers(data.filter(u => u.role === 'shopkeeper' || u.role === 'transporter' || u.role === 'deliveryboy'));
    });

    // Fetch Payouts
    const qPay = query(collection(db, 'payouts'), orderBy('createdAt', 'desc'));
    const unsubPay = onSnapshot(qPay, snap => {
      setPayouts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });

    return () => {
      unsubUsers();
      unsubPay();
    };
  }, []);

  const handleAddPayout = async (e) => {
    e.preventDefault();
    if (!selectedUser) {
      alert('कृपया दुकानदार किंवा ड्रायव्हर निवडा.');
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      alert('कृपया वैध रक्कम टाका.');
      return;
    }

    const uObj = users.find(u => u.id === selectedUser);
    const payeeName = uObj?.shopName || uObj?.name || 'User';
    const payeeRole = uObj?.role || 'vendor';
    const payeeUpi = uObj?.upiId || uObj?.phone || '';

    try {
      setSubmitting(true);
      await addDoc(collection(db, 'payouts'), {
        userId: selectedUser,
        payeeName,
        payeeRole,
        payeeUpi,
        amount: parseFloat(amount),
        paymentMode,
        transactionRef: transactionRef.trim(),
        notes: notes.trim(),
        status: 'Settled',
        createdAt: serverTimestamp(),
        date: new Date().toISOString().split('T')[0],
      });

      // Deduct/Settle user's pending revenue in Firestore
      if (uObj?.revenue) {
        const remaining = Math.max(0, (uObj.revenue || 0) - parseFloat(amount));
        await updateDoc(doc(db, 'users', selectedUser), { revenue: remaining });
      }

      setSelectedUser('');
      setAmount('');
      setTransactionRef('');
      setNotes('');
      setSubmitting(false);
      alert('पेमेंट / हिशोब (Payout) यशस्वीरीत्या नोंदवला गेला!');
    } catch (err) {
      setSubmitting(false);
      alert('Error: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('हा पेआऊट रेकॉर्ड डिलीट करायचा आहे का?')) {
      try {
        await deleteDoc(doc(db, 'payouts', id));
      } catch (err) {
        alert('Error: ' + err.message);
      }
    }
  };

  return (
    <div style={{ paddingBottom: 40 }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)', margin: '0 0 6px 0' }}>
          💳 Vendor & Driver Payouts (पेआऊट व कमिशन हिशोब)
        </h2>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          दुकानदार आणि ड्रायव्हर्सना हस्तांतरित केलेले पैसे (Payouts) आणि सेटलमेंटची नोंद ठेवा.
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 20 }}>
        {/* Record Payout Form */}
        <div className="card" style={{ height: 'fit-content' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconPlus size={18} /> नवीन पेआऊट नोंदवा (Record Payout)
          </h3>

          <form onSubmit={handleAddPayout} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>दुकानदार / ड्रायव्हर निवडा</label>
              <select
                value={selectedUser}
                onChange={e => setSelectedUser(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)' }}
                required
              >
                <option value="">-- निवडा --</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.role === 'shopkeeper' ? '🏪 ' : '🛵 '}
                    {u.shopName || u.name} ({u.role}) - बाकी: ₹{u.revenue || 0}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>पेमेंट दिलेली रक्कम (Amount ₹)</label>
              <input
                type="number"
                placeholder="e.g. 2500"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)' }}
                required
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>पेमेंट मोड (Mode)</label>
                <select
                  value={paymentMode}
                  onChange={e => setPaymentMode(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)' }}
                >
                  <option value="UPI">UPI / GPay</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cash">Cash (रोख)</option>
                </select>
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>UPI / UTR Ref No.</label>
                <input
                  type="text"
                  placeholder="Txn ID"
                  value={transactionRef}
                  onChange={e => setTransactionRef(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)' }}
                />
              </div>
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>टीप / Note (Optional)</label>
              <input
                type="text"
                placeholder="e.g. Weekly settlement"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)' }}
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={submitting} style={{ marginTop: 4 }}>
              {submitting ? 'जमा होत आहे...' : '💳 पेआऊट सेव्ह करा (Save Payout)'}
            </button>
          </form>
        </div>

        {/* Payout History List */}
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>पेआऊट इतिहास (Payout History)</h3>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>लोड होत आहे...</div>
          ) : payouts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>कोणताही पेआऊट रेकॉर्ड केलेला नाही.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left', color: 'var(--text-muted)' }}>
                    <th style={{ padding: '8px 12px' }}>तारीख</th>
                    <th style={{ padding: '8px 12px' }}>नाव & प्रकार</th>
                    <th style={{ padding: '8px 12px' }}>पेमेंट मोड & Ref</th>
                    <th style={{ padding: '8px 12px' }}>रक्कम (₹)</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map(p => (
                    <tr key={p.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{p.date}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ fontWeight: 800 }}>{p.payeeName}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {p.payeeRole === 'shopkeeper' ? '🏪 Shopkeeper' : '🛵 Delivery Partner'}
                          {p.payeeUpi && ` • UPI: ${p.payeeUpi}`}
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ fontWeight: 600 }}>{p.paymentMode}</div>
                        {p.transactionRef && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Ref: {p.transactionRef}</div>}
                      </td>
                      <td style={{ padding: '10px 12px', fontWeight: 800, color: 'var(--success)' }}>
                        ₹{p.amount?.toLocaleString('en-IN')}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                        <button
                          onClick={() => handleDelete(p.id)}
                          className="btn btn-danger btn-sm"
                          style={{ padding: '4px 8px' }}
                          title="Delete Payout"
                        >
                          <IconTrash size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
